import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {FilterButton} from 'shells/filter-button/filter-button';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {SelectTwoLineIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import {useDismissibleUserSetting} from '../../../../util/hooks/use-dismissible-user-setting';

import {useDeleteFilter} from './graphql/hooks/use-delete-saved-filter';
import {buildProspectContactsUrl, buildProspectLeadsUrl} from './helpers';

type Props = {
    activeFilters: FilterObject[];
    onRemoveAllFilters: () => void;
    isFilterDrawerOpen: boolean;
    onToggleFilterDrawerShown: () => void;
    savedFilters: TwoLineOptionWithIcon[];
    selectedFilter: TwoLineOptionWithIcon | null | undefined;
    setSelectedFilter: (arg1?: TwoLineOptionWithIcon | null | undefined) => void;
    refetchSavedFilters: () => Promise<any>;
    isLoadingSavedFilters: boolean;
};

const SAVED_FILTERS_DROPDOWN_WIDTH = 350;

const customNoOptionsMessage = {
    icon: <Illustration src='rocketship-star' width={150} />,
    text: 'There are currently no saved filters. Start by adding filters and saving them in the top corner.',
    cta: (
        <Link
            href='https://support.nutshell.com/en/articles/9669692-prospectoriq-find-leads-that-match-your-icp'
            variant='button-primary'
            newTab={true}
            size='big'
        >
            Learn more
        </Link>
    ),
} as const;

export function ProspectorPageControlBar(props: Props) {
    const {shouldShow: isViewingOnboarding} = useDismissibleUserSetting(
        'has_viewed_prospector_onboarding'
    );

    const containerRef = React.useRef(null);
    const [width] = useResizeObserver(containerRef.current);

    const shrinkButtons = width && width <= 950;

    const [filterToDelete, setFilterToDelete] = React.useState<string | null | undefined>(null);
    const {deleteFilter, isLoading, error} = useDeleteFilter();

    const handleDeleteFilter = () => {
        if (!filterToDelete) return;

        deleteFilter({id: filterToDelete}).then(() => {
            if (props.selectedFilter && props.selectedFilter.value === filterToDelete) {
                props.setSelectedFilter(null);
                // When deleting a preset that is currently selected, we wanna remove the current filters as well
                props.onRemoveAllFilters();
            }
            props.refetchSavedFilters();
            setFilterToDelete(null);
        });
    };

    return (
        <div ref={containerRef}>
            <ListPageControlBar
                leftSection={
                    <>
                        <StackLegacy isHorizontal={true} spacing={16} alignCenter={true}>
                            <FilterButton
                                isOpen={props.isFilterDrawerOpen}
                                onClick={props.onToggleFilterDrawerShown}
                                activeFilters={props.activeFilters}
                            />
                            <div style={{width: SAVED_FILTERS_DROPDOWN_WIDTH}} className='mb-4'>
                                <SelectTwoLineIcon
                                    isLoading={props.isLoadingSavedFilters}
                                    height={35}
                                    value={props.selectedFilter}
                                    onChange={props.setSelectedFilter}
                                    options={props.savedFilters}
                                    styles={{
                                        // @ts-expect-error
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 6,
                                            width: SAVED_FILTERS_DROPDOWN_WIDTH,
                                        }),
                                    }}
                                    placeholder='Select a saved filter…'
                                    shouldWrap={false}
                                    customNoOptionsMessage={customNoOptionsMessage}
                                    removeButton={{
                                        // @ts-expect-error
                                        onClick: (id) => setFilterToDelete(id),
                                    }}
                                />
                            </div>
                            {props.activeFilters.length > 0 && (
                                <Button
                                    size='small'
                                    variant='text-primary'
                                    onClick={() => {
                                        props.setSelectedFilter(null);
                                        props.onRemoveAllFilters();
                                    }}
                                    noPadding={true}
                                >
                                    Remove filters
                                </Button>
                            )}
                        </StackLegacy>
                        {/* Don’t show list links when onboarding – not helpful */}
                        {!isViewingOnboarding && (
                            <div className='pr-16 pos-absolute right-0'>
                                <StackLegacy isHorizontal={true} spacing={16} alignCenter={true}>
                                    <Link
                                        size='small'
                                        newTab={true}
                                        href={buildProspectContactsUrl(
                                            NutshellFrontendSettings.originApiIdProspector
                                        )}
                                        variant='primary'
                                        icon={ExternalLinkIcon}
                                    >
                                        {shrinkButtons
                                            ? 'People added'
                                            : 'People added via ProspectorIQ'}
                                    </Link>
                                    <Link
                                        size='small'
                                        newTab={true}
                                        href={buildProspectLeadsUrl(
                                            NutshellFrontendSettings.originApiIdProspector
                                        )}
                                        variant='primary'
                                        icon={ExternalLinkIcon}
                                    >
                                        {shrinkButtons
                                            ? 'Leads added'
                                            : 'Leads added via ProspectorIQ'}
                                    </Link>
                                </StackLegacy>
                            </div>
                        )}
                    </>
                }
            />
            {filterToDelete && (
                <ConfirmationDialog
                    title='Delete saved filter'
                    message='Are you sure you want to delete this saved filter?'
                    customSaveText={{initial: 'Delete', saving: 'Deleting…', complete: 'Deleted'}}
                    onConfirm={() => handleDeleteFilter()}
                    onCancel={() => setFilterToDelete(null)}
                    // @ts-expect-error
                    errorMessage={error}
                    isSaving={isLoading}
                    autoFocusCancelButton={false}
                />
            )}
        </div>
    );
}
